import { DataspaceDataSectionVm } from '../models/dataspace-workspace.models';

const SECTION_TITLES: { [key: string]: string } = {
  project: 'Projekt',
  day: 'Arbeitszeit',
  weather: 'Wetter',
  nuFirma: 'Ihre Firma',
  ausgefuehrteArbeiten: 'Ausgeführte Arbeiten',
  behinderungen: 'Behinderungen',
  geraete: 'Geräte',
  material: 'Material',
  weisungen: 'Weisungen',
  pruefungen: 'Prüfungen',
  verdeckteLeistungen: 'Verdeckte Leistungen',
  vorkommnisse: 'Vorkommnisse',
  interneFelder: 'Interne Felder',
  companyReports: 'Firmenberichte',
  personal: 'Personal',
  nachunternehmer: 'Nachunternehmer',
  progress: 'Fortschritt',
  anordnungen: 'Anordnungen',
  abweichungen: 'Abweichungen',
  wasserstaende: 'Wasserstände',
  aufmasse: 'Aufmaße',
  abnahmen: 'Abnahmen',
  dokumentation: 'Dokumentation',
  freitext: 'Freitext'
};

const DEFAULT_EMPTY_SUMMARY = 'Keine zusammenfassenden Angaben vorhanden.';

export function prettyDataspaceSectionTitle(sectionId: string): string {
  return SECTION_TITLES[sectionId] || sectionId;
}

export function buildDataspaceSections(source: any, allowedSectionIds: string[]): DataspaceDataSectionVm[] {
  const keys = Object.keys(source || {})
    .filter((key) => allowedSectionIds.includes(key))
    .filter((key) => key !== 'selectedSectionIds')
    .filter((key) => hasSectionContent(source[key]));
  return keys.map((key) => buildDataspaceSection(key, source[key], source));
}

export function buildDataspaceSection(sectionId: string, value: any, source?: any): DataspaceDataSectionVm {
  const summary = summarizeSection(sectionId, value, source);
  return {
    id: sectionId,
    title: prettyDataspaceSectionTitle(sectionId),
    value,
    summaryTitle: summary.summaryTitle,
    summaryLines: summary.summaryLines
  };
}

export function formatDataspaceSectionLabels(sectionIds: string[]): string {
  return sectionIds.map((sectionId) => prettyDataspaceSectionTitle(sectionId)).join(', ');
}

function summarizeSection(sectionId: string, value: any, source?: any): { summaryTitle: string; summaryLines: string[] } {
  switch (sectionId) {
    case 'project':
      return summarizeProject(value);
    case 'day':
      return summarizeDay(value, source);
    case 'weather':
      return summarizeWeather(value);
    case 'nuFirma':
      return summarizeNuFirma(value, source);
    case 'ausgefuehrteArbeiten':
      return summarizeArraySection('Arbeiten', value, ['beschreibung', 'arbeitsfortschritt', 'sonstiges']);
    case 'behinderungen':
      return summarizeArraySection('Behinderungen', value, ['ursache', 'betroffenerBereich', 'ausweichmassnahme']);
    case 'geraete':
      return summarizeArraySection('Geräte', value, ['bezeichnung', 'geraet', 'notiz', 'beschreibung']);
    case 'material':
      return summarizeArraySection('Materialpositionen', value, ['materialOderBauteil', 'menge', 'notiz']);
    case 'weisungen':
      return summarizeArraySection('Weisungen', value, ['inhalt', 'angewiesenVon', 'zeitpunkt']);
    case 'companyReports':
      return summarizeArraySection('Firmenberichte', value, ['companyName', 'firma', 'bericht', 'beschreibung']);
    case 'interneFelder':
      return summarizeInternalFields(value);
    case 'freitext':
      return summarizeTextBlock('Freitext', value);
    default:
      return Array.isArray(value)
        ? summarizeArraySection(prettyDataspaceSectionTitle(sectionId), value, [])
        : summarizeObjectSection(prettyDataspaceSectionTitle(sectionId), value);
  }
}

function summarizeProject(value: any): { summaryTitle: string; summaryLines: string[] } {
  const summaryTitle = firstValue(
    value?.bezeichnung,
    value?.bezeichnungDerBaumassnahme,
    value?.auftraggeber?.name,
    'Projekt'
  );
  const summaryLines = compactLines([
    value?.generalunternehmer ? `GU: ${value.generalunternehmer}` : '',
    value?.auftraggeber?.name ? `Auftraggeber: ${value.auftraggeber.name}` : '',
    value?.nachunternehmer ? `NU: ${value.nachunternehmer}` : '',
    value?.gewerk ? `Gewerk: ${value.gewerk}` : '',
    fullName(value?.bearbeiter) ? `Bearbeiter: ${fullName(value.bearbeiter)}` : ''
  ]);
  return fallbackSummary(summaryTitle, summaryLines);
}

function summarizeDay(value: any, source?: any): { summaryTitle: string; summaryLines: string[] } {
  const summaryTitle = value?.datum ? formatDateLabel(value.datum) : 'Tag ohne Datum';
  const workTime = formatTimeRange(value?.arbeitszeit?.beginn, value?.arbeitszeit?.ende);
  const summaryLines = compactLines([
    workTime ? `Arbeitszeit: ${workTime}` : '',
    formatTimeRange(source?.nuFirma?.schlechtesWetter?.beginn, source?.nuFirma?.schlechtesWetter?.ende)
      ? `Schlechtes Wetter: ${formatTimeRange(source?.nuFirma?.schlechtesWetter?.beginn, source?.nuFirma?.schlechtesWetter?.ende)}`
      : ''
  ]);
  return fallbackSummary(summaryTitle, summaryLines);
}

function summarizeWeather(value: any): { summaryTitle: string; summaryLines: string[] } {
  const tageslage = firstValue(value?.tageslage, 'Wetter');
  const temperature = formatTemperatureRange(value?.temperaturMinC, value?.temperaturMaxC);
  const summaryTitle = temperature ? `${tageslage} · ${temperature}` : tageslage;
  const summaryLines = compactLines([
    value?.niederschlagProzent !== undefined && value?.niederschlagProzent !== '' ? `Niederschlag: ${value.niederschlagProzent}%` : '',
    value?.windGeschwindigkeitKmh ? `Wind: ${value.windGeschwindigkeitKmh} km/h${value?.windRichtung ? ` ${value.windRichtung}` : ''}` : '',
    Array.isArray(value?.messungen) && value.messungen.length ? `${value.messungen.length} Messungen hinterlegt` : '',
    value?.bemerkung || ''
  ]);
  return fallbackSummary(summaryTitle, summaryLines);
}

function summarizeNuFirma(value: any, source?: any): { summaryTitle: string; summaryLines: string[] } {
  const workerCount = Array.isArray(value?.arbeitskraefte) ? value.arbeitskraefte.length : 0;
  const summaryTitle = firstValue(value?.firma?.name, 'NU-Angaben');
  const summaryLines = compactLines([
    source?.day?.vorarbeiter ? `Vorarbeiter: ${source.day.vorarbeiter}` : '',
    workerCount ? `${workerCount} Arbeitskräfte erfasst` : ''
  ]);
  return fallbackSummary(summaryTitle, summaryLines);
}

function summarizeInternalFields(value: any): { summaryTitle: string; summaryLines: string[] } {
  const summaryTitle = firstValue(value?.interneZuordnung, 'Interne Hinweise');
  const summaryLines = compactLines([
    value?.sollIstLeistung ? `Soll / Ist: ${value.sollIstLeistung}` : '',
    value?.produktiveZeitVerlustzeit ? `Produktiv / Verlust: ${value.produktiveZeitVerlustzeit}` : '',
    value?.internerVorgangsbezug ? `Vorgangsbezug: ${value.internerVorgangsbezug}` : '',
    value?.interneBewertungHinweis || ''
  ]);
  return fallbackSummary(summaryTitle, summaryLines);
}

function summarizeTextBlock(fallbackTitle: string, value: any): { summaryTitle: string; summaryLines: string[] } {
  const text = typeof value === 'string' ? value.trim() : '';
  return fallbackSummary(fallbackTitle, text ? [text] : []);
}

function summarizeArraySection(
  fallbackTitle: string,
  value: any,
  priorityKeys: string[]
): { summaryTitle: string; summaryLines: string[] } {
  const items = Array.isArray(value) ? value : [];
  const summaryTitle = items.length ? `${items.length} Einträge` : fallbackTitle;
  const summaryLines = items
    .slice(0, 3)
    .map((item) => summarizeArrayItem(item, priorityKeys))
    .filter(Boolean);
  return fallbackSummary(summaryTitle, summaryLines);
}

function summarizeObjectSection(fallbackTitle: string, value: any): { summaryTitle: string; summaryLines: string[] } {
  const pairs = extractPrimitivePairs(value).slice(0, 3);
  const summaryLines = pairs.map(([label, entryValue]) => `${label}: ${entryValue}`);
  return fallbackSummary(fallbackTitle, summaryLines);
}

function summarizeArrayItem(item: any, priorityKeys: string[]): string {
  if (item == null) {
    return '';
  }
  if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
    return String(item);
  }
  for (const key of priorityKeys) {
    const value = item?.[key];
    if (value !== undefined && value !== null && `${value}`.trim()) {
      return formatPrimitiveValue(value);
    }
  }
  const pairs = extractPrimitivePairs(item);
  if (!pairs.length) {
    return '';
  }
  return pairs
    .slice(0, 2)
    .map(([label, value]) => `${label}: ${value}`)
    .join(' · ');
}

function extractPrimitivePairs(value: any, prefix = ''): Array<[string, string]> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return [];
  }
  const pairs: Array<[string, string]> = [];
  Object.keys(value).forEach((key) => {
    const entry = value[key];
    const label = formatLabel(prefix ? `${prefix} ${key}` : key);
    if (entry == null || entry === '') {
      return;
    }
    if (typeof entry === 'string' || typeof entry === 'number' || typeof entry === 'boolean') {
      pairs.push([label, formatPrimitiveValue(entry)]);
      return;
    }
    if (!Array.isArray(entry)) {
      extractPrimitivePairs(entry, label).forEach((pair) => pairs.push(pair));
    }
  });
  return pairs;
}

function fallbackSummary(summaryTitle: string, summaryLines: string[]): { summaryTitle: string; summaryLines: string[] } {
  return {
    summaryTitle,
    summaryLines: summaryLines.length ? summaryLines : [DEFAULT_EMPTY_SUMMARY]
  };
}

function compactLines(lines: Array<string | null | undefined>): string[] {
  return lines.map((line) => `${line || ''}`.trim()).filter(Boolean);
}

function hasSectionContent(value: any): boolean {
  if (value == null) {
    return false;
  }
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return true;
  }
  if (Array.isArray(value)) {
    return value.some((entry) => hasSectionContent(entry));
  }
  if (typeof value === 'object') {
    return Object.values(value).some((entry) => hasSectionContent(entry));
  }
  return false;
}

function firstValue(...values: Array<string | null | undefined>): string {
  return values.find((value) => `${value || ''}`.trim()) || '';
}

function fullName(value: any): string {
  return [value?.vorname, value?.nachname].filter(Boolean).join(' ').trim();
}

function formatTemperatureRange(min: any, max: any): string {
  const hasMin = min !== undefined && min !== null && `${min}` !== '';
  const hasMax = max !== undefined && max !== null && `${max}` !== '';
  if (hasMin && hasMax) {
    return `${min} bis ${max} °C`;
  }
  if (hasMin) {
    return `ab ${min} °C`;
  }
  if (hasMax) {
    return `bis ${max} °C`;
  }
  return '';
}

function formatDateLabel(value: string): string {
  if (!value) {
    return '';
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-');
    return `${day}.${month}.${year}`;
  }
  return value;
}

function formatTimeRange(start?: string, end?: string): string {
  const normalizedStart = trimTime(start);
  const normalizedEnd = trimTime(end);
  if (normalizedStart && normalizedEnd) {
    return `${normalizedStart} - ${normalizedEnd}`;
  }
  return normalizedStart || normalizedEnd || '';
}

function trimTime(value?: string): string {
  if (!value) {
    return '';
  }
  return value.length >= 5 ? value.slice(0, 5) : value;
}

function formatLabel(value: string): string {
  return value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (match) => match.toUpperCase());
}

function formatPrimitiveValue(value: string | number | boolean): string {
  if (typeof value === 'boolean') {
    return value ? 'Ja' : 'Nein';
  }
  return `${value}`;
}
